import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Minus,
  Crown,
  Users,
  Zap,
  ShieldCheck,
  Gift,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const TIERS = [
  {
    id: "free",
    name: "FREE",
    price: "$0",
    cadence: "forever",
    icon: Zap,
    tagline: "Get in the door",
    cta: { label: "Start free", to: "/signup" },
    highlight: false,
  },
  {
    id: "pro",
    name: "PRO",
    price: "$10",
    cadence: "per month",
    icon: Crown,
    tagline: "The full operating system",
    cta: { label: "Start Pro trial", to: "/signup?plan=pro" },
    highlight: true,
  },
  {
    id: "team",
    name: "TEAM",
    price: "$20",
    cadence: "per month",
    icon: Users,
    tagline: "Run the whole operation",
    cta: { label: "Start Team trial", to: "/signup?plan=team" },
    highlight: false,
  },
];

// v = true / false / string label
const FEATURE_ROWS = [
  {
    group: "Core",
    items: [
      { label: "Habit tracker", free: "3 habits", pro: "Unlimited", team: "Unlimited" },
      { label: "Weekly tasks", free: "10 / week", pro: "Unlimited", team: "Unlimited" },
      { label: "Pipelines", free: "1", pro: "Unlimited", team: "Unlimited" },
      { label: "Custom tracker & list tabs", free: false, pro: true, team: true },
      { label: "Task history", free: "4 weeks", pro: "Unlimited", team: "Unlimited" },
    ],
  },
  {
    group: "Weekly OS",
    items: [
      { label: "Drag-and-drop reordering", free: false, pro: true, team: true },
      { label: "Priority + time scheduling", free: false, pro: true, team: true },
      { label: "Auto-rollover of undone tasks", free: false, pro: true, team: true },
      { label: "Focus mode timer", free: false, pro: true, team: true },
    ],
  },
  {
    group: "Intelligence",
    items: [
      { label: "Dashboard", free: "Basic", pro: "Full analytics", team: "Team + personal" },
      { label: "AI daily brief", free: false, pro: true, team: true },
      { label: "AI schedule generation", free: false, pro: true, team: true },
      { label: "Weekly printable briefing", free: false, pro: true, team: true },
      { label: "Google Calendar sync", free: false, pro: true, team: true },
    ],
  },
  {
    group: "Team",
    items: [
      { label: "Shared workspaces", free: false, pro: false, team: true },
      { label: "Task assignment to members", free: false, pro: false, team: true },
      { label: "Role-based permissions", free: false, pro: false, team: true },
      { label: "Team activity feed", free: false, pro: false, team: true },
      { label: "Team analytics", free: false, pro: false, team: true },
    ],
  },
  {
    group: "Account",
    items: [
      { label: "Referral rewards (2 = 1 year free)", free: true, pro: true, team: true },
      { label: "PIN lock + 7-day sessions", free: true, pro: true, team: true },
      { label: "Web push notifications", free: true, pro: true, team: true },
      { label: "Cancel anytime", free: true, pro: true, team: true },
    ],
  },
];

const FAQS = [
  {
    q: "Is there really a free plan?",
    a: "Yes — Free forever, no card required. You get the habit tracker (up to 3 habits), weekly tasks (10/week), one pipeline, and the basic dashboard. Upgrade only if and when you outgrow it.",
  },
  {
    q: "What's the 24-hour trial?",
    a: "When you sign up we unlock full Pro access for 24 hours — no card, no auto-charge. It's just so you can try everything before deciding on Free, Pro, or Team.",
  },
  {
    q: "How do referrals work?",
    a: "Every account gets a personal referral link. When two of your referrals become paying subscribers, you get a full year of your current plan free — and it repeats. Your friends get 50% off their first month.",
  },
  {
    q: "Can I switch plans later?",
    a: "Any time. Upgrade instantly, downgrade at the end of your billing cycle. Referrals and history follow the account, not the plan.",
  },
  {
    q: "How is billing handled?",
    a: "Stripe. Monthly, no long-term contracts. You can cancel from the Billing tab in one click; you keep access until the period ends.",
  },
  {
    q: "Is my data private?",
    a: "Your workspace is private to your account. On Team, you explicitly invite members and control their role. No public workspaces, no cross-sharing, ever.",
  },
  {
    q: "Do you offer refunds?",
    a: "If something breaks or you're unhappy in the first 14 days of a paid plan, email us and we'll refund the last charge — no forms, no gymnastics.",
  },
];

function Cell({ v }) {
  if (v === true) {
    return (
      <div className="flex items-center justify-center">
        <Check size={16} className="text-hud" data-testid="pricing-cell-yes" />
      </div>
    );
  }
  if (v === false) {
    return (
      <div className="flex items-center justify-center">
        <Minus size={14} className="text-muted-foreground/50" data-testid="pricing-cell-no" />
      </div>
    );
  }
  return (
    <div className="text-center text-sm mono uppercase tracking-wide" data-testid="pricing-cell-text">
      {v}
    </div>
  );
}

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="hud-frame p-5 sm:p-6 transition-colors hover:bg-[hsl(var(--surface))/0.4]"
      data-testid={`pricing-faq-${idx}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left"
        aria-expanded={open}
        data-testid={`pricing-faq-toggle-${idx}`}
      >
        <span className="font-display text-base sm:text-lg font-medium leading-snug">{q}</span>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-180 text-hud" : ""
          }`}
        />
      </button>
      {open && (
        <p
          className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed"
          data-testid={`pricing-faq-answer-${idx}`}
        >
          {a}
        </p>
      )}
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const authed = user && user !== false;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="pricing-page">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/92 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-logo">
            <span className="status-dot" />
            <div>
              <div className="font-display font-bold text-lg uppercase leading-none tracking-tight">
                Command<span className="text-oud">·</span>Center
              </div>
              <div className="serif-mark text-[11px] text-muted-foreground mt-1 tracking-wide">
                For CEO&apos;s Only
              </div>
            </div>
          </Link>
          <span className="flex-1" />
          {authed ? (
            <Link
              to="/billing"
              className="hud-btn hud-btn-primary !text-[10px]"
              data-testid="pricing-manage-plan"
            >
              MANAGE PLAN <ArrowRight size={10} />
            </Link>
          ) : (
            <>
              <Link
                to="/signin"
                className="hud-btn !text-[10px]"
                data-testid="pricing-signin"
              >
                SIGN IN
              </Link>
              <Link
                to="/signup"
                className="hud-btn hud-btn-primary !text-[10px]"
                data-testid="pricing-signup"
              >
                START FREE TRIAL <ArrowRight size={10} />
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pt-16 lg:pt-24 pb-10 text-center">
        <div className="kpi-label flex items-center justify-center gap-2 mb-5">
          <ShieldCheck size={11} /> Pricing · Every Plan
        </div>
        <h1
          className="display font-bold leading-[0.95] tracking-tight uppercase"
          style={{ fontSize: "clamp(2.25rem, 6vw, 4.75rem)" }}
        >
          Start free.<br />
          <span className="text-hud">_</span>Scale when you&apos;re ready.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Three plans. No hidden add-ons. Cancel any time. Refer two paying users and
          run on us for a full year — repeatable.
        </p>
      </section>

      {/* Tier cards */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.id}
                className={`hud-frame p-7 relative flex flex-col ${
                  t.highlight ? "ring-2 ring-[hsl(var(--oxblood))] bg-[hsl(var(--surface))/0.5]" : ""
                }`}
                data-testid={`pricing-tier-${t.id}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 mono text-[9px] uppercase tracking-[0.28em] bg-[hsl(var(--oxblood))] text-white px-3 py-1">
                    Most Popular
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Icon size={18} className={t.highlight ? "text-hud" : "text-muted-foreground"} />
                  <div className="mono text-xs uppercase tracking-[0.28em]">{t.name}</div>
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <div
                    className="display font-bold leading-none"
                    style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}
                  >
                    {t.price}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    {t.cadence}
                  </div>
                </div>
                <div className="mt-3 text-sm text-muted-foreground">{t.tagline}</div>
                <div className="hud-divider my-6" />
                <Link
                  to={authed ? "/billing" : t.cta.to}
                  className={`hud-btn text-sm w-full justify-center ${
                    t.highlight ? "hud-btn-primary" : ""
                  }`}
                  data-testid={`pricing-cta-${t.id}`}
                >
                  {authed ? "GO TO BILLING" : t.cta.label.toUpperCase()} <ArrowRight size={12} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-20">
        <div className="kpi-label text-center mb-8">Compare · Feature by Feature</div>

        {/* Desktop table */}
        <div className="hidden md:block hud-frame overflow-hidden" data-testid="pricing-compare-desktop">
          <div className="grid grid-cols-4 border-b border-border bg-[hsl(var(--surface))/0.4]">
            <div className="p-4 mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Feature
            </div>
            {TIERS.map((t) => (
              <div
                key={t.id}
                className={`p-4 text-center mono text-[10px] uppercase tracking-[0.28em] ${
                  t.highlight ? "text-hud" : ""
                }`}
              >
                {t.name} · {t.price}
              </div>
            ))}
          </div>

          {FEATURE_ROWS.map((section) => (
            <div key={section.group}>
              <div className="p-3 px-4 border-b border-border/60 bg-[hsl(var(--surface))/0.2] mono text-[9px] uppercase tracking-[0.32em] text-muted-foreground">
                {section.group}
              </div>
              {section.items.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-4 border-b border-border/40 last:border-b-0 hover:bg-[hsl(var(--surface))/0.25]"
                >
                  <div className="p-4 text-sm">{row.label}</div>
                  <div className="p-4 flex items-center justify-center">
                    <Cell v={row.free} />
                  </div>
                  <div className="p-4 flex items-center justify-center bg-[hsl(var(--surface))/0.25]">
                    <Cell v={row.pro} />
                  </div>
                  <div className="p-4 flex items-center justify-center">
                    <Cell v={row.team} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Mobile: stacked per-tier */}
        <div className="md:hidden space-y-6" data-testid="pricing-compare-mobile">
          {TIERS.map((t) => (
            <div key={t.id} className={`hud-frame p-5 ${t.highlight ? "ring-1 ring-[hsl(var(--oxblood))]" : ""}`}>
              <div className="flex items-baseline justify-between">
                <div className="mono text-[11px] uppercase tracking-[0.28em]">{t.name}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {t.price} · {t.cadence}
                </div>
              </div>
              <div className="hud-divider my-4" />
              {FEATURE_ROWS.map((section) => (
                <div key={section.group} className="mb-4 last:mb-0">
                  <div className="mono text-[9px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
                    {section.group}
                  </div>
                  <ul className="space-y-1.5">
                    {section.items.map((row) => {
                      const v = row[t.id];
                      return (
                        <li key={row.label} className="flex items-start gap-2 text-sm">
                          {v === true ? (
                            <Check size={14} className="text-hud mt-0.5 flex-shrink-0" />
                          ) : v === false ? (
                            <Minus size={14} className="text-muted-foreground/40 mt-0.5 flex-shrink-0" />
                          ) : (
                            <span className="mono text-[10px] uppercase tracking-widest text-hud mt-1 flex-shrink-0">
                              ·
                            </span>
                          )}
                          <span className={v === false ? "text-muted-foreground/60 line-through decoration-1" : ""}>
                            {row.label}
                            {typeof v === "string" && (
                              <span className="ml-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                ({v})
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Referral hook */}
      <section className="border-y border-border bg-[hsl(var(--surface))/0.4]">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-14 text-center">
          <div className="kpi-label flex items-center justify-center gap-2 mb-4">
            <Gift size={11} /> Referral · The Loop
          </div>
          <h2 className="display font-bold uppercase tracking-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}>
            Refer two paying friends. <span className="text-hud">Get a year free.</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground max-w-xl mx-auto">
            They save 50% off their first month. You get 12 months free — and it repeats
            every time two more join.
          </p>
          <Link
            to="/signup"
            className="hud-btn hud-btn-primary mt-8 !px-6 !py-4 text-base inline-flex"
            data-testid="pricing-referral-cta"
          >
            CLAIM YOUR REFERRAL CODE <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-20">
        <div className="kpi-label text-center mb-2">FAQ · Common Questions</div>
        <h2
          className="display font-bold uppercase tracking-tight text-center mb-10"
          style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
        >
          Things people <span className="text-hud">ask.</span>
        </h2>
        <div className="space-y-3" data-testid="pricing-faq-list">
          {FAQS.map((f, i) => (
            <Faq key={f.q} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border">
        <div className="tread-plate h-1.5" />
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-16 text-center">
          <div className="kpi-label">Ready · Begin</div>
          <h2
            className="display font-bold uppercase tracking-tight mt-4 leading-[0.95]"
            style={{ fontSize: "clamp(1.75rem, 5vw, 3.25rem)" }}
          >
            One screen.<br />
            <span className="text-hud">_</span>Your entire operation.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={authed ? "/billing" : "/signup"}
              className="hud-btn hud-btn-primary text-base !px-6 !py-4"
              data-testid="pricing-final-cta"
            >
              {authed ? "GO TO BILLING" : "START FREE TRIAL"} <ArrowRight size={14} />
            </Link>
            <Link to="/" className="hud-btn text-base !px-6 !py-4" data-testid="pricing-back-home">
              BACK TO HOME
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="font-display font-bold text-base uppercase tracking-tight">
              Command<span className="text-hud">·</span>Center
            </div>
            <div className="mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground mt-1">
              For CEO&apos;s Only
            </div>
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            © {new Date().getFullYear()} Command Center · Built for operators
          </div>
        </div>
      </footer>
    </div>
  );
}
